/**
 * WordPress dependencies
 */
import { applyFilters } from '@wordpress/hooks';

export const DEFAULT_CLIENT_LIMIT_PER_ROOM = 3;

// Retry delays after poll failures.
// The disconnect dialog shows after all retries are exhausted, then retries
// continue at DISCONNECT_DIALOG_RETRY_MS.
export const ERROR_RETRY_DELAYS_SOLO_MS = [
	2000, 4000, 8000, 12000,
	// Solo: 26s total retry time solo before dialog
];
export const ERROR_RETRY_DELAYS_WITH_COLLABORATORS_MS = [
	1000, 2000, 4000, 8000,
	// With collaborators: 15s total retry time before dialog
];

// How often to automatically retry the connection when in the disconnect dialog.
export const DISCONNECT_DIALOG_RETRY_MS = 30000;

// When a user manually retries on the disconnection dialog, the amount of time
// until the next automatic retry attempt.
export const MANUAL_RETRY_INTERVAL_MS = 15000;

const MAX_ENCODED_UPDATE_SIZE_IN_BYTES = 1 * 1024 * 1024; // 1 MB

// The server validates the base64-encoded `data` string against a 1 MB
// maxLength. Base64 encodes three raw bytes as four characters, so cap the raw
// Yjs update size to the largest value that cannot exceed the server limit.
export const MAX_UPDATE_SIZE_IN_BYTES =
	Math.floor( MAX_ENCODED_UPDATE_SIZE_IN_BYTES / 4 ) * 3;

// Corresponds with server-side
// WP_HTTP_Polling_Sync_Server::MAX_ROOMS_PER_REQUEST.
export const MAX_ROOMS_PER_REQUEST = 50;

// Corresponds with server-side
// WP_HTTP_Polling_Sync_Server::MAX_BODY_SIZE, with 1 MiB of headroom for
// serialization details and future metadata.
export const MAX_SYNC_REQUEST_BODY_SIZE_IN_BYTES = 15 * 1024 * 1024;

// Keep a single maximum-sized encoded update plus room metadata sendable if a
// request-body-too-large response forces the client to shrink its retry budget.
export const MIN_SYNC_REQUEST_BODY_SIZE_LIMIT_IN_BYTES = 2 * 1024 * 1024;

const DEFAULT_POLLING_INTERVAL_IN_MS = 4000; // 4 seconds
const DEFAULT_POLLING_INTERVAL_WITH_COLLABORATORS_IN_MS = 1000; // 1 second

function getFilteredPollingInterval(
	hookName: string,
	defaultInterval: number
): number {
	const filteredInterval = applyFilters( hookName, defaultInterval );

	if (
		typeof filteredInterval !== 'number' ||
		! Number.isFinite( filteredInterval ) ||
		filteredInterval <= 0
	) {
		return defaultInterval;
	}

	return Math.min( filteredInterval, defaultInterval );
}

export const POLLING_INTERVAL_IN_MS = getFilteredPollingInterval(
	'sync.pollingManager.pollingInterval',
	DEFAULT_POLLING_INTERVAL_IN_MS
);

export const POLLING_INTERVAL_WITH_COLLABORATORS_IN_MS =
	getFilteredPollingInterval(
		'sync.pollingManager.pollingIntervalWithCollaborators',
		DEFAULT_POLLING_INTERVAL_WITH_COLLABORATORS_IN_MS
	);

// Must be less than the server-side AWARENESS_TIMEOUT (30 s) to avoid
// false disconnects when the tab is in the background.
export const POLLING_INTERVAL_BACKGROUND_TAB_IN_MS = 25 * 1000; // 25 seconds
